import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Trash2, CheckCircle2, XCircle, Plus, Pencil, Upload, FileText } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

interface MagicPrompt {
  id: string;
  category: string;
  prompt: string;
  gender: 'male' | 'female' | 'any';
  is_active: boolean;
  created_at: string;
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'ชาย (Male)' },
  { value: 'female', label: 'หญิง (Female)' },
  { value: 'any', label: 'ทั้งสอง (Any)' },
];

const MagicButtonManagement = () => {
  const { toast } = useToast();
  const [prompts, setPrompts] = useState<MagicPrompt[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [category, setCategory] = useState("");
  const [prompt, setPrompt] = useState("");
  const [gender, setGender] = useState<'male' | 'female' | 'any'>('any');
  const [activeOnly, setActiveOnly] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editCategory, setEditCategory] = useState("");
  const [editPrompt, setEditPrompt] = useState("");
  const [editSaving, setEditSaving] = useState(false);

  // Bulk import state
  const [bulkCategory, setBulkCategory] = useState("");
  const [bulkGender, setBulkGender] = useState<'male' | 'female' | 'any'>('any');
  const [bulkText, setBulkText] = useState("");
  const [bulkSaving, setBulkSaving] = useState(false);

  // Parse bulk text into lines (ignoring empty lines)
  const bulkLines = useMemo(() => {
    if (!bulkText.trim()) return [];
    return bulkText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line.length > 0);
  }, [bulkText]);

  const categories = useMemo(() => {
    const unique = new Set(prompts.map((p) => p.category).filter(Boolean));
    return Array.from(unique);
  }, [prompts]);

  const fetchPrompts = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from("magic_prompts")
        .select("id, category, prompt, gender, is_active, created_at")
        .order("created_at", { ascending: false });

      if (activeOnly) {
        query = query.eq("is_active", true);
      }

      const { data, error } = await query;
      if (error) throw error;
      setPrompts((data || []) as MagicPrompt[]);
    } catch (error: any) {
      console.error("Error fetching magic prompts:", error);
      toast({
        title: "Error",
        description: "Failed to load Magic Button prompts",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPrompts();
  }, [activeOnly]);

  const handleAddPrompt = async () => {
    if (!category.trim() || !prompt.trim()) {
      toast({
        title: "Error",
        description: "Category and prompt are required",
        variant: "destructive",
      });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from("magic_prompts")
        .insert({
          category: category.trim(),
          prompt: prompt.trim(),
          gender: gender,
          is_active: true,
        })
        .select();

      if (error) throw error;

      toast({
        title: "Added",
        description: "Magic prompt has been added",
      });

      setCategory("");
      setPrompt("");
      setGender('any');
      fetchPrompts();
    } catch (error: any) {
      console.error("Error adding magic prompt:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to add prompt",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase
        .from("magic_prompts")
        .update({ is_active: !isActive })
        .eq("id", id);

      if (error) throw error;

      fetchPrompts();
    } catch (error: any) {
      console.error("Error updating magic prompt:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update prompt",
        variant: "destructive",
      });
    }
  };

  const handleStartEdit = (item: MagicPrompt) => {
    setEditingId(item.id);
    setEditCategory(item.category);
    setEditPrompt(item.prompt);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditCategory("");
    setEditPrompt("");
  };

  const handleSaveEdit = async (id: string) => {
    if (!editCategory.trim() || !editPrompt.trim()) {
      toast({
        title: "Error",
        description: "Category and prompt are required",
        variant: "destructive",
      });
      return;
    }

    setEditSaving(true);
    try {
      const { error } = await supabase
        .from("magic_prompts")
        .update({
          category: editCategory.trim(),
          prompt: editPrompt.trim(),
        })
        .eq("id", id);

      if (error) throw error;

      toast({
        title: "Updated",
        description: "Magic prompt has been updated",
      });

      handleCancelEdit();
      fetchPrompts();
    } catch (error: any) {
      console.error("Error updating magic prompt:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to update prompt",
        variant: "destructive",
      });
    } finally {
      setEditSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this magic prompt?")) return;
    try {
      const { error } = await supabase
        .from("magic_prompts")
        .delete()
        .eq("id", id);

      if (error) throw error;
      fetchPrompts();
    } catch (error: any) {
      console.error("Error deleting magic prompt:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete prompt",
        variant: "destructive",
      });
    }
  };

  const handleBulkImport = async () => {
    if (!bulkCategory.trim()) {
      toast({
        title: "Error",
        description: "Please select or enter a category",
        variant: "destructive",
      });
      return;
    }

    if (bulkLines.length === 0) {
      toast({
        title: "Error",
        description: "Please paste prompts (one per line)",
        variant: "destructive",
      });
      return;
    }

    const genderLabel = GENDER_OPTIONS.find(g => g.value === bulkGender)?.label || bulkGender;
    if (!confirm(`Import ${bulkLines.length} prompts to category "${bulkCategory}" (${genderLabel})?`)) {
      return;
    }

    setBulkSaving(true);
    try {
      const promptsToInsert = bulkLines.map((line) => ({
        category: bulkCategory.trim(),
        prompt: line,
        gender: bulkGender,
        is_active: true,
      }));

      const { error } = await supabase
        .from("magic_prompts")
        .insert(promptsToInsert);

      if (error) throw error;

      toast({
        title: "Imported",
        description: `Successfully imported ${bulkLines.length} prompts`,
      });

      setBulkText("");
      fetchPrompts();
    } catch (error: any) {
      console.error("Error bulk importing prompts:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to import prompts",
        variant: "destructive",
      });
    } finally {
      setBulkSaving(false);
    }
  };

  return (
    <Card variant="glass">
      <CardHeader>
        <CardTitle>Magic Button</CardTitle>
        <CardDescription>Manage the prompt library for viral random generation</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <Tabs defaultValue="single" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="single" className="gap-2">
              <Plus className="h-4 w-4" />
              Add Single
            </TabsTrigger>
            <TabsTrigger value="bulk" className="gap-2">
              <Upload className="h-4 w-4" />
              Bulk Import
            </TabsTrigger>
          </TabsList>

          {/* Single Add Tab */}
          <TabsContent value="single" className="space-y-4 mt-4">
            <div className="grid gap-3 md:grid-cols-[200px_120px_1fr]">
              <div className="space-y-2">
                <label className="text-sm font-medium">Category</label>
                <Input
                  placeholder="e.g. Lifestyle, Food, Travel"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  list="magic-categories"
                />
                <datalist id="magic-categories">
                  {categories.map((c) => (
                    <option key={c} value={c} />
                  ))}
                </datalist>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Gender</label>
                <select
                  value={gender}
                  onChange={(e) => setGender(e.target.value as 'male' | 'female' | 'any')}
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                >
                  {GENDER_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Prompt</label>
                <Textarea
                  placeholder="Prompt that will be used by Magic Button"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  className="min-h-[96px]"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleAddPrompt} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                Add Prompt
              </Button>
            </div>
          </TabsContent>

          {/* Bulk Import Tab */}
          <TabsContent value="bulk" className="space-y-4 mt-4">
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-[1fr_150px]">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Category</label>
                  <Input
                    placeholder="e.g. Lifestyle, Food, Travel"
                    value={bulkCategory}
                    onChange={(e) => setBulkCategory(e.target.value)}
                    list="magic-categories-bulk"
                  />
                  <datalist id="magic-categories-bulk">
                    {categories.map((c) => (
                      <option key={c} value={c} />
                    ))}
                  </datalist>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Gender</label>
                  <select
                    value={bulkGender}
                    onChange={(e) => setBulkGender(e.target.value as 'male' | 'female' | 'any')}
                    className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  >
                    {GENDER_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Paste Prompts (one per line)
                </label>
                <Textarea
                  placeholder="Paste your prompts here, one per line...&#10;&#10;Example:&#10;@character is walking on the beach at sunset&#10;@character is dancing in the rain&#10;@character is cooking in a modern kitchen"
                  value={bulkText}
                  onChange={(e) => setBulkText(e.target.value)}
                  className="min-h-[200px] font-mono text-sm"
                />
              </div>

              {/* Preview count */}
              <div className="p-4 rounded-lg bg-muted/50 border border-white/10">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Prompts to import:</span>
                  <Badge variant={bulkLines.length > 0 ? "default" : "secondary"} className="text-lg px-3">
                    {bulkLines.length}
                  </Badge>
                </div>
                {bulkLines.length > 0 && (
                  <div className="mt-3 text-xs text-muted-foreground">
                    <span>Preview (first 3):</span>
                    <ul className="mt-1 space-y-1">
                      {bulkLines.slice(0, 3).map((line, i) => (
                        <li key={i} className="truncate pl-2 border-l-2 border-primary/50">
                          {line.length > 80 ? line.substring(0, 80) + "..." : line}
                        </li>
                      ))}
                      {bulkLines.length > 3 && (
                        <li className="pl-2 text-muted-foreground/60">
                          ...and {bulkLines.length - 3} more
                        </li>
                      )}
                    </ul>
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  onClick={handleBulkImport}
                  disabled={bulkSaving || bulkLines.length === 0 || !bulkCategory.trim()}
                  className="gap-2"
                >
                  {bulkSaving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4" />
                  )}
                  Import {bulkLines.length} Prompts
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Filter */}
        <div className="flex items-center">
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={(e) => setActiveOnly(e.target.checked)}
            />
            Show active only
          </label>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : prompts.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-8">
              No prompts yet. Add the first one above.
            </div>
          ) : (
            prompts.map((item) => (
              <div
                key={item.id}
                className="p-4 rounded-lg border border-white/10 bg-white/5 flex flex-col md:flex-row md:items-center gap-3 justify-between"
              >
                <div className="space-y-2 min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="text-[10px] uppercase tracking-wide">
                      {item.category}
                    </Badge>
                    <Badge
                      className={`text-[10px] ${
                        item.gender === 'male'
                          ? 'bg-blue-500/10 text-blue-400 border-blue-500/20'
                          : item.gender === 'female'
                          ? 'bg-pink-500/10 text-pink-400 border-pink-500/20'
                          : 'bg-purple-500/10 text-purple-400 border-purple-500/20'
                      }`}
                    >
                      {item.gender === 'male' ? 'ชาย' : item.gender === 'female' ? 'หญิง' : 'ทั้งสอง'}
                    </Badge>
                    {item.is_active ? (
                      <Badge className="bg-green-500/10 text-green-500 border-green-500/20 text-[10px]">
                        Active
                      </Badge>
                    ) : (
                      <Badge className="bg-muted text-muted-foreground text-[10px]">
                        Disabled
                      </Badge>
                    )}
                    {editingId === item.id && (
                      <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20 text-[10px]">
                        Editing
                      </Badge>
                    )}
                  </div>

                  {editingId === item.id ? (
                    <div className="space-y-2">
                      <Input
                        value={editCategory}
                        onChange={(e) => setEditCategory(e.target.value)}
                        placeholder="Category"
                      />
                      <Textarea
                        value={editPrompt}
                        onChange={(e) => setEditPrompt(e.target.value)}
                        className="min-h-[80px]"
                        placeholder="Prompt"
                      />
                    </div>
                  ) : (
                    <p className="text-sm text-foreground/90 line-clamp-2">{item.prompt}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {editingId === item.id ? (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleSaveEdit(item.id)}
                        className="gap-1"
                        disabled={editSaving}
                      >
                        {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                        Save
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleCancelEdit}
                        className="gap-1"
                      >
                        <XCircle className="h-4 w-4" />
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleStartEdit(item)}
                        className="gap-1"
                      >
                        <Pencil className="h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleToggleActive(item.id, item.is_active)}
                        className="gap-1"
                      >
                        {item.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
                        {item.is_active ? "Disable" : "Enable"}
                      </Button>
                    </>
                  )}
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => handleDelete(item.id)}
                    disabled={editingId === item.id}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MagicButtonManagement;
